import { watch } from 'vue'
import {
  pollBulkImportOnce,
  BULK_IMPORT_POLL_INTERVAL_MS,
  BULK_IMPORT_POLL_MAX_MS,
  delay,
  type BulkImportPollTick,
} from '@/composables/bulkImport/bulkImportPoll'
import { useActiveBulkImportsStore } from '@/stores/activeBulkImports'
import { useBulkImportApi } from '@/services/bulkImport'
import { ApiErrorException } from '@/types/apiErrors'
import type { BulkImportEntity } from '@/types/bulkImport'
import i18n from '@/plugins/i18n'

const TERMINAL_STATUSES = ['completed', 'failed', 'invalid'] as const

const NON_RETRYABLE_STATUSES = [401, 403, 404]
const MAX_CONSECUTIVE_RATE_LIMITS = 5
const RATE_LIMIT_BACKOFF_MAX_MS = 30_000

function errorStatus(err: unknown): number {
  return err instanceof ApiErrorException ? err.getStatus() : 0
}

function pollFailedMessage(): string {
  return i18n.global.t('corp.bulk_import.toast.poll_failed_description')
}

function pollTimeoutMessage(): string {
  return i18n.global.t('corp.bulk_import.toast.poll_timeout_description')
}

/**
 * Instanciar EXATAMENTE UMA vez (em App.vue). Cada chamada cria seu proprio
 * estado de loop (Map activeLoops + watcher de store.pollable), entao multiplas
 * instancias fariam polling paralelo dos mesmos imports.
 */
export function useBulkImportProgressPoll(): { start(): void; stop(): void } {
  const activeLoops = new Map<string, AbortController>()
  const givenUp = new Set<string>()

  function startLoopForItem(id: string, entity: BulkImportEntity): void {
    if (activeLoops.has(id) || givenUp.has(id)) return

    const controller = new AbortController()
    activeLoops.set(id, controller)
    const { signal } = controller
    const deadline = Date.now() + BULK_IMPORT_POLL_MAX_MS

    const api = useBulkImportApi()
    const store = useActiveBulkImportsStore()
    let consecutiveRateLimits = 0

    const giveUp = (failed: boolean, message?: string) => {
      givenUp.add(id)
      activeLoops.delete(id)
      if (failed) {
        store.markPollFailed(id, message ?? pollFailedMessage())
      } else {
        store.dismiss(id)
      }
    }

    const handleRateLimited = async (): Promise<boolean> => {
      consecutiveRateLimits++
      if (consecutiveRateLimits >= MAX_CONSECUTIVE_RATE_LIMITS) {
        giveUp(true)
        return true
      }
      const backoff = Math.min(
        BULK_IMPORT_POLL_INTERVAL_MS * 2 ** consecutiveRateLimits,
        RATE_LIMIT_BACKOFF_MAX_MS,
      )
      await delay(backoff, signal)
      return false
    }

    const run = async () => {
      while (!signal.aborted) {
        if (Date.now() >= deadline) {
          giveUp(true, pollTimeoutMessage())
          break
        }

        let tick: BulkImportPollTick
        try {
          tick = await pollBulkImportOnce(
            () => api.fetchImportStatus(entity, id),
            [...TERMINAL_STATUSES],
          )
        } catch (err) {
          if (signal.aborted) break
          if (NON_RETRYABLE_STATUSES.includes(errorStatus(err))) {
            giveUp(true)
            break
          }
          await delay(BULK_IMPORT_POLL_INTERVAL_MS, signal)
          continue
        }

        if (signal.aborted) break

        if (tick.import) {
          store.upsertFromImport(tick.import)
        }

        if (tick.terminal) {
          activeLoops.delete(id)
          break
        }

        if (tick.rateLimited) {
          if (await handleRateLimited()) break
        } else {
          consecutiveRateLimits = 0
          await delay(BULK_IMPORT_POLL_INTERVAL_MS, signal)
        }
      }
    }

    run().catch(() => {
      activeLoops.delete(id)
    })
  }

  let stopWatch: (() => void) | null = null

  function start(): void {
    if (stopWatch) return

    const store = useActiveBulkImportsStore()

    stopWatch = watch(
      () => store.pollable,
      (pollableImports) => {
        const pollableIds = new Set(pollableImports.map((item) => item.id))
        for (const item of pollableImports) {
          startLoopForItem(item.id, item.entity)
        }
        for (const [loopId, controller] of activeLoops) {
          if (!pollableIds.has(loopId)) {
            controller.abort()
            activeLoops.delete(loopId)
          }
        }
      },
      { immediate: true },
    )
  }

  function stop(): void {
    if (stopWatch) {
      stopWatch()
      stopWatch = null
    }
    for (const controller of activeLoops.values()) {
      controller.abort()
    }
    activeLoops.clear()
  }

  return { start, stop }
}
